import React from 'react';

import { useLocalSearchParams } from 'expo-router';
import { MenuSkeletonLoader } from 'features/menu/components/menu-tile-grid/menu-tile-grid-loading';
import { useTargetItemIds } from 'features/menu/hooks/use-target-item-ids';
import { MenuSection } from 'features/menu/pages/menu-object-page/menu-section';
import { MenuSectionPage } from 'features/menu/pages/menu-section-page/menu-section-page';
import { useStoreContext } from 'features/store';
import { View } from 'react-native';

import { FloatingViewCartFab } from 'components/floating-view-cart-fab';
import { MenuSectionCatalogPreview } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-section-catalog-preview';
import { useUnavailableItemId } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-unavailable-item-id';
import { useControlNoStoreStoreLocatorRedirect } from 'experiments/deeplinks-flow-feature/treatment/features/store/hooks/use-control-no-store-store-locator-redirect';
import {
  useDeeplinksFlowExperiment,
  useExposeDeeplinksFlowExperiment,
} from 'experiments/deeplinks-flow-feature/use-deeplinks-flow-experiment';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { appendObjectToQueryString } from 'navigation/utils';
import { routes } from 'utils/routing';

export default function MenuSectionRoute() {
  const { noStoreSelected } = useStoreContext();
  const {
    isEnabled: isDeeplinksFlowEnabled,
    isLoading: isExperimentLoading,
  } = useDeeplinksFlowExperiment();

  useExposeDeeplinksFlowExperiment(noStoreSelected && !isExperimentLoading);

  if (noStoreSelected) {
    // Wait for Amplitude so treatment never flashes the control store locator.
    if (isExperimentLoading) {
      return <MenuSkeletonLoader />;
    }

    return isDeeplinksFlowEnabled ? (
      <TreatmentSectionNoStoreCatalogPreview />
    ) : (
      <ControlSectionNoStoreStoreLocatorRedirect />
    );
  }

  return <MenuSectionInner />;
}

const MenuSectionInner = React.memo(() => {
  const enableUnifiedMenu = useEnableUnifiedMenu();

  return (
    <View testID="menu-section-page" style={{ flex: 1 }}>
      {enableUnifiedMenu ? <MenuSectionPage /> : <MenuSection />}
      <FloatingViewCartFab safeAreaBottom />
    </View>
  );
});

/**
 * Control (deeplinks-flow-feature): open the legacy full store locator, returning
 * to this section after selection. If the guest closes without picking a store,
 * dismiss this host so a storeless empty section does not remain.
 */
function ControlSectionNoStoreStoreLocatorRedirect() {
  const { id = '' } = useLocalSearchParams<{ id: string }>();
  const targetItemIds = useTargetItemIds();
  const unavailableItemId = useUnavailableItemId();
  const redirectUrl = appendObjectToQueryString(`${routes.menu}/section/${id}`, {
    ...(targetItemIds.length ? { targetItemIds: targetItemIds.join(',') } : {}),
    ...(unavailableItemId ? { unavailableItemId } : {}),
  });

  useControlNoStoreStoreLocatorRedirect(redirectUrl);

  return <MenuSkeletonLoader />;
}

/**
 * Treatment: open the generic OneMenu section with its products and a select-
 * restaurant CTA. After store selection, MenuSectionPage re-checks availability
 * and may show unavailable-section recovery.
 */
function TreatmentSectionNoStoreCatalogPreview() {
  const { id = '' } = useLocalSearchParams<{ id: string }>();
  const targetItemIds = useTargetItemIds();
  const unavailableItemId = useUnavailableItemId();

  return (
    <MenuSectionCatalogPreview
      sectionId={id}
      targetItemIds={targetItemIds}
      unavailableItemId={unavailableItemId}
    />
  );
}
